<template>
  <div class="header-nav">
    <!-- 左侧：测试计划标题 -->
    <div class="nav-left">
      <div class="page-title">
        <div class="title-icon"></div>
        <div class="title-content">
          <h1 class="title-text">AI精准测试分析</h1>
          <div v-if="props.task.planName" class="subtitle" :title="props.task.planName">
            {{ props.task.planName }}
          </div>
        </div>
      </div>
    </div>

    <!-- 中间：参数信息 -->
    <div class="nav-center">
      <div class="inline-params">
        <div class="inline-param-item">
          <span class="inline-param-label">项目</span>
          <div v-if="projectInfoLoading" class="inline-loading">
            <div class="inline-skeleton"></div>
          </div>
          <span v-else class="inline-param-value" :title="projectInfo?.name || props.task.projectName">
            {{ projectInfo?.name || props.task.projectName || '--' }}
          </span>
        </div>

        <div class="inline-param-divider"></div>

        <div class="inline-param-item">
          <span class="inline-param-label">Git</span>
          <div v-if="gitlabProjectInfoLoading" class="inline-loading">
            <div class="inline-skeleton"></div>
          </div>
          <span v-else class="inline-param-value" :title="gitlabProjectInfo?.name">
            {{ gitlabProjectInfo?.name || '--' }}
          </span>
        </div>

        <div class="inline-param-divider"></div>

        <div class="inline-param-item">
          <span class="inline-param-label">分支</span>
          <span class="inline-branch-compare">
            <span class="inline-branch-name">{{ props.task.analysisParams?.from_ref || 'develop' }}</span>
            <icon-vue icon="mdi:arrow-right" class="inline-branch-arrow" />
            <span class="inline-branch-name">{{ props.task.analysisParams?.to_ref || 'main' }}</span>
          </span>
        </div>
      </div>
    </div>

    <!-- 右侧：操作按钮组 -->
    <div class="nav-right">
      <div class="action-buttons">
        <!-- 分析控制按钮 -->
        <a-tooltip v-if="!aiAnalysis.isAnalyzing.value" placement="bottom">
          <template #title>
            {{ aiAnalysis.isCompleted.value || aiAnalysis.isError.value ? '重新分析' : '开始分析' }}
          </template>
          <button class="icon-btn icon-btn-primary" @click="handleRetryAnalysis">
            <icon-vue
              :icon="
                aiAnalysis.isCompleted.value || aiAnalysis.isError.value
                  ? 'mdi:refresh-circle'
                  : 'mdi:play-circle-outline'
              "
              class="icon-lg"
            />
          </button>
        </a-tooltip>

        <a-tooltip v-else placement="bottom">
          <template #title>停止分析</template>
          <button class="icon-btn icon-btn-danger" @click="handleStopAnalysis">
            <icon-vue icon="mdi:stop-circle-outline" class="icon-lg animate-pulse" />
          </button>
        </a-tooltip>

        <!-- 分隔线 -->
        <div class="action-divider"></div>

        <!-- 最小化按钮 -->
        <a-tooltip placement="bottom">
          <template #title>最小化</template>
          <button class="icon-btn icon-btn-secondary" @click="handleMinimize">
            <icon-vue icon="mdi:window-minimize" class="icon-md" />
          </button>
        </a-tooltip>

        <!-- 关闭按钮 -->
        <a-tooltip placement="bottom">
          <template #title>关闭</template>
          <button class="icon-btn icon-btn-secondary" @click="handleClose">
            <icon-vue icon="mdi:close" class="icon-md" />
          </button>
        </a-tooltip>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { projectApi } from '@/api/common/project';
import type { AIAnalysisRequestParams } from '@/api/precision-new/analysis';
import { gitApi } from '@/api/precision-new/git';
import type { useAIAnalysis } from '@/sites/precision-new/composables/useAIAnalysis';
import type { AIAnalysisTask } from '@/store/modules/ai-analysis-tasks';
import type { SysProject } from '@/types/common';
import type { GitProject } from '@/types/precision-new/git';
import { Icon as IconVue } from '@iconify/vue';
import { message } from 'ant-design-vue';
import { inject, onMounted, ref } from 'vue';

const props = defineProps<{
  task: AIAnalysisTask;
}>();

const emit = defineEmits<{
  (e: 'minimize'): void;
  (e: 'close'): void;
}>();

// 从父组件注入共享的 AI 分析实例
const aiAnalysis = inject('aiAnalysis') as ReturnType<typeof useAIAnalysis>;

// 项目信息
const projectInfo = ref<SysProject | null>(null);
const projectInfoLoading = ref(false);

// GitLab项目信息
const gitlabProjectInfo = ref<GitProject | null>(null);
const gitlabProjectInfoLoading = ref(false);

// 获取项目信息
async function fetchProjectInfo() {
  if (!props.task.projectId) {
    return;
  }

  try {
    projectInfoLoading.value = true;
    const response = await projectApi.getInfo(props.task.projectId);
    if (response.code === 0) {
      projectInfo.value = response.data;
    }
  } catch (error) {
    console.error('获取项目信息失败:', error);
  } finally {
    projectInfoLoading.value = false;
  }
}

// 获取GitLab项目信息
async function fetchGitlabProjectInfo() {
  const gitlabProjectId = props.task.analysisParams?.gitlab_project_id;
  if (!gitlabProjectId) {
    return;
  }

  try {
    gitlabProjectInfoLoading.value = true;
    const response = await gitApi.getProjectInfo(Number(gitlabProjectId));
    if (response.code === 0) {
      gitlabProjectInfo.value = response.data;
    }
  } catch (error) {
    console.error('获取GitLab项目信息失败:', error);
  } finally {
    gitlabProjectInfoLoading.value = false;
  }
}

function handleRetryAnalysis() {
  if (aiAnalysis.isAnalyzing.value || !props.task.analysisParams) {
    return;
  }

  const params: AIAnalysisRequestParams = props.task.analysisParams;
  aiAnalysis.startAnalysis(params);
}

function handleStopAnalysis() {
  aiAnalysis.cancelAnalysis();
  message.info('已停止分析');
}

function handleMinimize() {
  emit('minimize');
}

function handleClose() {
  emit('close');
}

onMounted(() => {
  fetchProjectInfo();
  fetchGitlabProjectInfo();

  // 只有当任务是新创建的（idle状态）且有分析参数时，才自动开始分析
  // 已完成或出错的任务，需要用户手动点击重新分析
  if (props.task.analysisParams && props.task.status === 'idle' && props.task.progress === 0) {
    handleRetryAnalysis();
  }
});
</script>

<style scoped>
/* Header 导航栏 - 直接作为 drawer title 内容 */
.header-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  gap: 24px;
  width: 100%;
  min-height: 56px;
}

/* 左侧：标题区 */
.nav-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-icon {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  animation: pulse 2s infinite;
  flex-shrink: 0;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.title-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.title-text {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
}

.subtitle {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
}

/* 中间：参数信息 */
.nav-center {
  flex: 1;
  display: flex;
  justify-content: center;
  max-width: 700px;
  margin: 0 auto;
}

.inline-params {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 10px 20px;
  background: rgba(248, 250, 252, 0.8);
  border-radius: 8px;
  border: 1px solid rgba(226, 232, 240, 0.6);
  backdrop-filter: blur(4px);
}

.inline-param-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.inline-param-label {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  white-space: nowrap;
}

.inline-param-value {
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.inline-loading {
  display: flex;
  align-items: center;
}

.inline-skeleton {
  height: 14px;
  width: 80px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 3px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.inline-param-divider {
  width: 1px;
  height: 16px;
  background: #e2e8f0;
}

.inline-branch-compare {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace;
}

.inline-branch-name {
  padding: 3px 8px;
  background: #e2e8f0;
  border-radius: 6px;
  font-size: 11px;
  color: #475569;
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.inline-branch-arrow {
  width: 12px;
  height: 12px;
  color: #64748b;
}

/* 右侧：操作按钮组 */
.nav-right {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-divider {
  width: 1px;
  height: 24px;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(226, 232, 240, 0.8) 20%,
    rgba(226, 232, 240, 1) 50%,
    rgba(226, 232, 240, 0.8) 80%,
    transparent 100%
  );
  margin: 0 4px;
  align-self: center;
  flex-shrink: 0;
}

/* 图标按钮 - 精致版 */
.icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: transparent;
  position: relative;
  overflow: hidden;
}

.icon-btn::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  transition: all 0.3s ease;
  opacity: 0;
}

.icon-btn:hover {
  transform: translateY(-1px) scale(1.02);
}

.icon-btn:active {
  transform: translateY(0) scale(0.98);
}

/* 主要按钮 - 开始/重新分析 */
.icon-btn-primary {
  background: linear-gradient(135deg, #818cf8 0%, #a78bfa 100%);
  color: white;
  box-shadow:
    0 2px 4px rgba(129, 140, 248, 0.2),
    0 1px 2px rgba(129, 140, 248, 0.1);
}

.icon-btn-primary::before {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0) 100%);
}

.icon-btn-primary:hover {
  box-shadow:
    0 4px 12px rgba(129, 140, 248, 0.35),
    0 2px 4px rgba(129, 140, 248, 0.2);
}

.icon-btn-primary:hover::before {
  opacity: 1;
}

/* 危险按钮 - 停止分析 */
.icon-btn-danger {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
  box-shadow:
    0 2px 4px rgba(245, 87, 108, 0.25),
    0 1px 2px rgba(245, 87, 108, 0.15);
  animation: pulse-glow 2s ease-in-out infinite;
}

.icon-btn-danger::before {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0) 100%);
}

.icon-btn-danger:hover {
  box-shadow:
    0 4px 12px rgba(245, 87, 108, 0.4),
    0 2px 4px rgba(245, 87, 108, 0.25);
  animation: none;
}

.icon-btn-danger:hover::before {
  opacity: 1;
}

@keyframes pulse-glow {
  0%,
  100% {
    box-shadow:
      0 2px 4px rgba(245, 87, 108, 0.25),
      0 1px 2px rgba(245, 87, 108, 0.15);
  }
  50% {
    box-shadow:
      0 2px 8px rgba(245, 87, 108, 0.35),
      0 1px 4px rgba(245, 87, 108, 0.2),
      0 0 12px rgba(245, 87, 108, 0.15);
  }
}

/* 次要按钮 - 毛玻璃效果 */
.icon-btn-secondary {
  background: rgba(248, 250, 252, 0.8);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(226, 232, 240, 0.6);
  color: #64748b;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.icon-btn-secondary::before {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 100%);
}

.icon-btn-secondary:hover {
  background: rgba(241, 245, 249, 0.95);
  border-color: rgba(203, 213, 225, 0.8);
  color: #334155;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.icon-btn-secondary:hover::before {
  opacity: 1;
}

/* 图标大小 */
.icon-lg {
  font-size: 20px;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.icon-md {
  font-size: 18px;
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.05));
}

/* 响应式 */
@media (max-width: 1200px) {
  .nav-center {
    max-width: 600px;
  }

  .inline-params {
    gap: 16px;
    padding: 8px 16px;
  }

  .inline-param-value {
    max-width: 100px;
  }
}

@media (max-width: 768px) {
  .header-nav {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .nav-left,
  .nav-center,
  .nav-right {
    width: 100%;
  }

  .inline-params {
    justify-content: center;
    flex-wrap: wrap;
  }

  .action-btn {
    width: 100%;
    max-width: 300px;
    margin: 0 auto;
  }
}
</style>
